import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../../../utils/ToastSettings";
import { PricesModel } from "../../../../models/Financial/Prices/PricesModel";
import { PlanAgentsCategoriesService } from "../../../../services/Financial/Plans/PlanAgentsCategories/PlanAgentsCategoriesServices";

interface ContextProps {
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  categoriesRegistrait: any[];
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  isLoadingModal: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: any) => Promise<boolean>;
}

export const PlanAgentsCategoriesContext = createContext<ContextProps>(
  {} as ContextProps,
);

interface PlanAgentsCategoriesProviderProps {
  children: ReactNode;
}

export const PlanAgentsCategoriesProvider = ({
  children,
}: PlanAgentsCategoriesProviderProps) => {
  const [params, setParams] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingModal, setIsLoadingModal] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [categoriesRegistrait, setCategoriesRegistrait] = useState<any[]>([]);

  const service = new PlanAgentsCategoriesService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/financial/plans")) {
      return false;
    }

    try {
      setIsLoadingModal(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoadingModal(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setCategoriesRegistrait(_Response?.data?.items);
      setPages(_Response?.data?.pagination);
      return setError("");
    } catch (e) {
      setIsLoadingModal(false);
      setError("Não foi possível carregar os preços do sistema.");
    }
  }, [params, location.pathname]);

  const handleFetch = async (data: any) => {
    try {
      setIsLoadingModal(true);
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      setIsLoadingModal(false);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors,
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar plano.");
      setIsLoadingModal(false);
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <PlanAgentsCategoriesContext.Provider
      value={{
        params,
        categoriesRegistrait,
        setParams,
        pages,
        setPages,
        isLoading,
        isLoadingModal,
        error,
        setError,
        handleList,
        handleFetch,
      }}
    >
      {children}
    </PlanAgentsCategoriesContext.Provider>
  );
};

export const usePlanAgentsCategories = () =>
  useContext(PlanAgentsCategoriesContext);
